import { useState } from "react";
import { Form, Row, Col } from "react-bootstrap";
import type { ClientFormValues, ProjectVariableField } from "../types";
import type { RateTableOption } from "../api";

export interface ClientFormProps {
  id: string;
  initialValues: ClientFormValues;
  rateTables: RateTableOption[];
  projectVariables: ProjectVariableField[];
  isSubmitting: boolean;
  onSubmit: (values: ClientFormValues) => void;
}

const UdfField = ({
  field,
  value,
  onChange,
}: {
  field: ProjectVariableField;
  value: string;
  onChange: (v: string) => void;
}): JSX.Element => {
  const common = {
    id: `udf-${field.name}`,
    value,
    required: field.required,
    onChange: (
      e: React.ChangeEvent<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      >
    ) => onChange(e.target.value),
  };

  if (field.selectOptions.length > 0) {
    return (
      <Form.Control as="select" {...common}>
        <option value="" />
        {field.selectOptions.map((o) => (
          <option key={o.value} value={o.value}>
            {o.key}
          </option>
        ))}
      </Form.Control>
    );
  }
  if (field.variableType === "number") {
    return (
      <Form.Control
        type="number"
        min={field.minimum}
        max={field.maximum}
        {...common}
      />
    );
  }
  if (field.variableType === "date") {
    return <Form.Control type="date" {...common} />;
  }
  if (field.maximum && field.maximum > 256) {
    return <Form.Control as="textarea" {...common} />;
  }
  return <Form.Control type="text" {...common} />;
};

export const ClientForm = ({
  id,
  initialValues,
  rateTables,
  projectVariables,
  isSubmitting,
  onSubmit,
}: ClientFormProps): JSX.Element => {
  const [values, setValues] = useState<ClientFormValues>(initialValues);

  const set = <K extends keyof ClientFormValues>(
    key: K,
    v: ClientFormValues[K]
  ) => setValues((prev) => ({ ...prev, [key]: v }));

  const setUdf = (name: string, v: string) =>
    setValues((prev) => ({
      ...prev,
      udfValues: { ...prev.udfValues, [name]: v },
    }));

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitting) return;
    onSubmit(values);
  };

  return (
    <Form id={id} data-testid={id} onSubmit={handleSubmit}>
      <Form.Group className="mb-3">
        <Form.Label htmlFor="client-name">Name</Form.Label>
        <Form.Control
          id="client-name"
          type="text"
          required
          value={values.name}
          onChange={(e) => set("name", e.target.value)}
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label htmlFor="client-domain">Website Domain</Form.Label>
        <Form.Control
          id="client-domain"
          type="text"
          placeholder="company.com"
          value={values.domain}
          onChange={(e) => set("domain", e.target.value)}
        />
      </Form.Group>

      <Row>
        <Col sm={6}>
          <Form.Group className="mb-3">
            <Form.Label htmlFor="client-msa-date">MSA Date</Form.Label>
            <Form.Control
              id="client-msa-date"
              type="date"
              value={values.msaDate}
              onChange={(e) => set("msaDate", e.target.value)}
            />
          </Form.Group>
        </Col>
        {rateTables.length > 1 && (
          <Col sm={6}>
            <Form.Group className="mb-3">
              <Form.Label htmlFor="client-rate-table">
                Preferred Rates
              </Form.Label>
              <Form.Control
                id="client-rate-table"
                as="select"
                value={values.rateTableId}
                onChange={(e) => set("rateTableId", e.target.value)}
              >
                <option value="" />
                {rateTables.map((rt) => (
                  <option key={rt.id} value={rt.id}>
                    {rt.name}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          </Col>
        )}
      </Row>

      {projectVariables.map((field) => (
        <Form.Group key={field.name} className="mb-3">
          <Form.Label htmlFor={`udf-${field.name}`}>{field.label}</Form.Label>
          <UdfField
            field={field}
            value={values.udfValues[field.name] ?? ""}
            onChange={(v) => setUdf(field.name, v)}
          />
        </Form.Group>
      ))}
    </Form>
  );
};

export default ClientForm;
